import { SearchResult } from '@lessonPlanner/commons';
import { Language } from '@lessonPlanner/enums';

import { Portal } from '../enums/portal.enum';
import { DLLObjective, FormContents } from '../types/page';
import { GetPromptResponsePayload } from '../types/prompt';

function formatAssessmentTypes(
  assessmentTypes: string[] | undefined,
  assessmentType: string,
): string {
  if (assessmentTypes?.length) {
    return assessmentTypes.join('; ');
  }
  return assessmentType;
}

function removeUndefinedValues(
  obj: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  );
}

export function mapChatParamValues(
  formContents: FormContents,
  newLessonFormat: string,
  fetchedLinks?: SearchResult[],
  portal?: Portal,
): Partial<GetPromptResponsePayload> {
  const getValue = function (key: string, defaultValue?: unknown): unknown {
    if (!(key in formContents)) {
      return undefined;
    }
    const value = formContents[key];
    return value !== undefined && value !== '' ? value : defaultValue;
  };

  const topicKeys = ['topic', 'quizTopic', 'enTopic'];
  const topicValue = topicKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;
  const assessmentTypeKeys = ['assessmentType', 'quizAssessmentType'];
  const assessmentTypeValue = assessmentTypeKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;
  const assessmentTypesValue = getValue('assessmentTypes');
  const additionalInformationKeys = [
    'additionalInformation',
    'quizAdditionalInformation',
  ];
  const additionalInformationValue = additionalInformationKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;
  const unpackTypeValue = getValue('guUnpackType');
  const itemToUnpackValue =
    getValue('guItemToUnpack') || getValue('itemToUnpack');
  const unpackItemValue = getValue('unpackItem') || itemToUnpackValue;

  const lpObjectiveKeys = [
    'lpObjective',
    'lpObjective2',
    'lpObjective3',
    ...(portal === Portal.PHILIPPINES ? ['lpObjective4'] : []),
  ];

  const mgObjectiveKeys = ['mgObjective', 'mgObjective2', 'mgObjective3'];

  const quizObjectiveKeys = [
    'quizObjective',
    'quizObjective2',
    'quizObjective3',
  ];

  const lpKeysExist = lpObjectiveKeys.every((key) => key in formContents);
  const lpObjectivesString = lpKeysExist
    ? lpObjectiveKeys
        .map((key) => getValue(key, ' '))
        .filter((value) => value !== undefined && value !== '')
        .join(', ')
    : undefined;

  const mgKeysExist = mgObjectiveKeys.every((key) => key in formContents);
  const mgObjectivesString = mgKeysExist
    ? mgObjectiveKeys
        .map((key) => getValue(key, ' '))
        .filter((value) => value !== undefined && value !== '')
        .join(', ')
    : undefined;

  const quizObjectivesString = quizObjectiveKeys.every((k) => k in formContents)
    ? quizObjectiveKeys
        .map((k) => getValue(k, ' '))
        .filter(Boolean)
        .join(', ')
    : undefined;

  const objectiveValue =
    (getValue('lpLessonFormat') && lpObjectivesString) ||
    (getValue('mgLessonFormat') && mgObjectivesString) ||
    quizObjectivesString;

  const gradeLevelKeys = [
    'gradeLevel',
    'lpGradeLevel',
    'mgGradeLevel',
    'objGradeLevel',
    'seGradeLevel',
    'dllGradeLevel',
    'guGradeLevel',
    'teGradeLevel',
    'quizGradeLevel',
    'enGradeLevel',
  ];
  const gradeLevel = gradeLevelKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const subjectKeys = [
    'subject',
    'lpSubject',
    'mgSubject',
    'objSubject',
    'seSubject',
    'dllSubject',
    'guSubject',
    'teSubject',
    'quizSubject',
  ];
  const subject = subjectKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const languageKeys = [
    'language',
    'lplanguage',
    'mgLanguage',
    'objlanguage',
    'seLanguage',
    'dllLanguage',
    'quizLanguage',
  ];
  const language = languageKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const competencyValue = getValue('objCompetency') as string;

  const difficultyKeys = ['difficulty', 'quizDifficulty'];
  const difficulty = difficultyKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const timeKeys = ['time', 'quizTime'];
  const time = timeKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const materialKeys = ['materials', 'lpMaterials'];
  const materials = materialKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const objectiveKeys = ['objective', 'seObjective', 'objObjectives'];
  const objective = objectiveKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const referenceText = getValue('referenceText') as string;
  const day = getValue('day') as string;
  const quarterlyTheme = getValue('quarterlyTheme') as string;
  const subTheme = getValue('subTheme') as string;
  const sessionObjectives = getValue('sessionObjectives') as string;
  const topics = getValue('topics') as {
    topic: string;
    subtopics: string[];
  }[];
  const dllObjectives = getValue('dllObjectives') as DLLObjective[];

  const courseTitle = getValue('courseTitle') as string[];
  const courseTopic = getValue('courseTopic') as string[];
  const contentStandard = getValue('contentStandard') as string[];
  const performanceStandard = getValue('performanceStandard') as string[];

  const theme = getValue('theme', ' ') as string;
  const context = getValue('context', ' ') as string;
  const attributes = getValue('attributes', []) as string[];
  const goals = getValue('goals', ' ') as string;

  const filteredTopics =
    topics &&
    topics.map((topicObject) => ({
      topic: topicObject.topic,
      subtopics: topicObject.subtopics.filter((subtopic) => subtopic !== ''),
    }));

  let learningCompetencies = '';
  const rawCompetencies = getValue('learningCompetencies') as
    | string[]
    | undefined;
  if (rawCompetencies === undefined) {
    learningCompetencies = undefined;
  } else if (rawCompetencies && Array.isArray(rawCompetencies)) {
    learningCompetencies = rawCompetencies
      .map(
        (competency, index) =>
          `Learning Competency ${index + 1}: ${competency}`,
      )
      .join(', ');
  }

  const formatSubtopics = (subtopics, topicIndex) =>
    subtopics
      .map(
        (subtopic, subIndex) => `${topicIndex + 1}.${subIndex + 1} ${subtopic}`,
      )
      .join(', ');

  const formatTopicWithSubtopics = (topicObj, index) => {
    const formattedSubtopics = formatSubtopics(topicObj.subtopics || [], index);
    return `Topic ${index + 1}: ${topicObj.topic} (${formattedSubtopics})`;
  };

  const formatOptionalTopic = (topic) => {
    return topic ? `Topic: ${topic}` : ' ';
  };
  let formattedTopic = topicValue as string;
  if (topicValue && (topicValue as string).length === 0) {
    formattedTopic = formatOptionalTopic(topicValue as string);
  }

  const formatObjectives = (dayObjective) =>
    dayObjective &&
    dayObjective
      .filter((objective) => objective !== '')
      .map((objective, subIndex) => `${subIndex + 1}: ${objective}`)
      .join(', ');

  const formatObjectivesPerDay = (dllObjective) => {
    const dayObjectives = dllObjective.dayObjectives || [];
    if (dayObjectives.length === 0) return '';

    const formattedDllObjective = formatObjectives(dayObjectives);
    return `${dllObjective.day}: ${formattedDllObjective}`;
  };

  const topicsWithSubtopics =
    filteredTopics && filteredTopics.map(formatTopicWithSubtopics).join(', ');

  const dailyObjectivesArray = dllObjectives
    ? dllObjectives.map(formatObjectivesPerDay).filter((obj) => obj !== '')
    : [];

  const dailyObjectives = dllObjectives
    ? dailyObjectivesArray.length > 0
      ? dailyObjectivesArray.join(' -|; ')
      : ''
    : undefined;

  const formatSnippetsString = (links: SearchResult[]) => {
    if (!links || links.length === 0) return undefined;
    return links
      .map((link, index) => `([#${index + 1}] ${link.snippet})`)
      .join('; ');
  };

  const linkSnippets = fetchedLinks ? formatSnippetsString(fetchedLinks) : '';

  function formatPptSections(sections: { title: string; slides: number }[]) {
    if (!sections?.length) return undefined;
    return sections
      .map((section, index) => `Section ${index + 1}: ${section.title}`)
      .join('; ');
  }
  const sections = formatPptSections(
    getValue('sections') as { title: string; slides: number }[],
  );

  const slideNumber = (() => {
    const value = getValue('slideNumber', 1);
    return value !== undefined ? value.toString() : undefined;
  })();
  const doc = getValue('doc') as string;
  const modality = getValue('modality', ' ') as string;

  const numberKeys = ['number', 'quizNumber'];
  const number = numberKeys.reduce(
    (acc, key) => acc || getValue(key, 10),
    '',
  ) as string;

  const curriculum = getValue('curriculum') as string;

  const design = getValue('design') as string;

  // American LP
  const strategiesKeys = ['lpStrategies', 'strategies', 'quizStrategies'];
  const strategies = strategiesKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const specificStandardsKeys = [
    'lpSpecificStandards',
    'objSpecificStandards',
    'seSpecificStandards',
    'specificStandards',
    'stSpecificStandards',
    'quizSpecificStandards',
  ];
  const specificStandards = specificStandardsKeys.reduce(
    (acc, key) => acc || getValue(key, ' '),
    '',
  ) as string;

  const curriculumFrameworkKeys = [
    'curriculumFramework',
    'lpCurriculumFramework',
    'objCurriculumFramework',
    'stCurriculumFramework',
    'quizCurriculumFramework',
  ];
  const curriculumFramework = curriculumFrameworkKeys.reduce(
    (acc, key) => acc || getValue(key),
    '',
  ) as string;

  // Montessori LP
  const title = getValue('lpTitle', ' ') as string;
  const classProgram = getValue('lpClassProgram', ' ') as string;
  const prerequisites = getValue('lpPrerequisites', ' ') as string;

  const keyAreaKeys = ['lpKeyArea', 'lpKeyArea2', 'lpKeyArea3'];
  const keyAreaValues = keyAreaKeys
    .map((key) => getValue(key))
    .filter((value) => value !== undefined && value !== '')
    .join(', ') as string;

  const lpKeyAreaString = keyAreaValues.length > 0 ? keyAreaValues : undefined;
  const formatBehavioralVerbs = (verbs) => {
    if (verbs === undefined) {
      return undefined;
    }
    if (!verbs || !Array.isArray(verbs) || verbs.length === 0) return ' ';

    return verbs
      .map(
        (verb) => `${verb.layer}-${verb.domain}-${verb.category}-${verb.name}`,
      )
      .join(', ');
  };
  const cognitiveVerbs = formatBehavioralVerbs(
    getValue('objCognitiveVerbs') as {
      layer: string;
      domain: string;
      category: string;
      name: string;
    }[],
  );
  const affectiveVerbs = formatBehavioralVerbs(
    getValue('objAffectiveVerbs') as {
      layer: string;
      domain: string;
      category: string;
      name: string;
    }[],
  );
  const psychomotorVerbs = formatBehavioralVerbs(
    getValue('objPsychomotorVerbs') as {
      layer: string;
      domain: string;
      category: string;
      name: string;
    }[],
  );

  // Helper to format a single verb-item pair
  const formatPair = (verb, item) => {
    if (!verb) return null;

    let itemValue = '';
    if (typeof item === 'object' && item !== null) {
      itemValue = item.name || item.value || JSON.stringify(item);
    } else {
      itemValue = String(item || '');
    }

    return `${verb.layer}-${verb.domain}-${verb.category}-${verb.name}:${itemValue}`;
  };

  // Find all pairs by pattern and format them
  const formatAllAffectivePairs = (verbPrefix, itemPrefix) => {
    const pairs = [];

    // Get all keys that could be verbs or items
    const keys = Object.keys(formContents);

    // Find all verb keys matching our pattern
    const verbKeys = keys.filter(
      (key) =>
        key.startsWith(verbPrefix) &&
        Array.isArray(formContents[key]) &&
        formContents[key].length > 0,
    );
    // For each verb key, find the matching item key
    verbKeys.forEach((verbKey) => {
      const suffix = verbKey.replace(verbPrefix, '');
      const itemKey = itemPrefix + suffix;

      if (itemKey in formContents) {
        const verbs = formContents[verbKey];
        const items = formContents[itemKey];

        // Format each verb-item pair
        if (verbs && items) {
          const formattedPairs = verbs
            .map((verb, idx) =>
              formatPair(verb, idx < items.length ? items[idx] : ''),
            )
            .filter(Boolean);

          if (formattedPairs.length > 0) {
            pairs.push(formattedPairs.join(', '));
          }
        }
      }
    });

    return pairs.length > 0 ? pairs.join('; ') : ' ';
  };

  // Format the affective values and attitudes
  const affectiveValues = formatAllAffectivePairs(
    'objAffectiveValuesVerbs',
    'objValuesList',
  );
  const affectiveAttitudes = formatAllAffectivePairs(
    'objAffectiveAttitudesVerbs',
    'objAttitudesList',
  );

  return removeUndefinedValues({
    gradeLevel,
    subject,
    materials,
    time,
    referenceText,
    day,
    quarterlyTheme,
    subTheme,
    sessionObjectives,
    difficulty,
    language: language as Language,
    objective: objectiveValue || objective,
    objectives: objective || dailyObjectives,
    competencies: competencyValue,
    topic: formattedTopic,
    topics: topicsWithSubtopics,
    assessmentType: formatAssessmentTypes(
      assessmentTypesValue as string[],
      assessmentTypeValue as string,
    ),
    additionalInformation: additionalInformationValue,
    courseTitle,
    courseTopic,
    contentStandard,
    performanceStandard,
    learningCompetencies,
    linkSnippets: JSON.stringify(linkSnippets),
    theme,
    context,
    attributes,
    goals,
    curriculum,
    sections,
    doc,
    modality,
    number,
    design,
    slideNumber,
    cognitiveVerbs,
    affectiveVerbs,
    psychomotorVerbs,
    affectiveValues,
    affectiveAttitudes,
    //montessoriLP specific values
    title,
    classProgram,
    prerequisites,
    keyAreas: lpKeyAreaString,
    // American LP
    strategies,
    specificStandards,
    curriculumFramework,
    // Learning Competency
    unpackType: unpackTypeValue,
    unpackItem: unpackItemValue,
  });
}
